import React, { useState, useEffect, useRef } from 'react';
import { Card, Tooltip, Button, Modal, Tag, Avatar, notification } from 'antd';
import { 
  UserOutlined, 
  EyeOutlined,
  DeleteOutlined,
  LockOutlined,
  UnlockOutlined
} from '@ant-design/icons';

const SeatingLayout = ({ seatingLayout, onSeatingChange, students, isArranging, showSeatNumbers = true, seatNumberColor = '#374151' }) => {
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [currentStudent, setCurrentStudent] = useState(null);
  // 使用ref避免在dragStart期间触发重渲染导致拖拽中断
  const draggedStudentRef = useRef(null);
  const draggedFromRef = useRef(null);
  const [dragOverPosition, setDragOverPosition] = useState(null);
  const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, rowIndex: -1, colIndex: -1, seat: null });

  // 组件加载时的调试信息（已移除日志）
  useEffect(() => {}, []);

  // 开始拖拽（对齐课程表实现：仅依赖组件状态，不依赖dataTransfer）
  const handleDragStart = (e, rowIndex, colIndex) => {
    const seat = seatingLayout[rowIndex][colIndex];
    if (!seat.student || seat.isEmpty) {
      e.preventDefault();
      return;
    }

    
    draggedStudentRef.current = seat.student;
    draggedFromRef.current = { row: rowIndex, col: colIndex };
    // 仅设置必要的拖拽属性，避免浏览器兼容性问题
    try {
      e.dataTransfer.effectAllowed = 'move';
      // 一些浏览器需要至少设置一个数据项才能触发后续dragover/drop事件
      e.dataTransfer.setData('text/plain', 'seat');
    } catch (_) {}
    
    e.currentTarget.style.opacity = '0.5';
  };

  // 拖拽结束
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    draggedStudentRef.current = null;
    draggedFromRef.current = null;
    setDragOverPosition(null);
  };

  // 拖拽悬停
  const handleDragOver = (e, rowIndex, colIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    const seat = seatingLayout[rowIndex][colIndex];
    
    if (!seat.isEmpty) {
      e.dataTransfer.dropEffect = 'move';
      setDragOverPosition({ row: rowIndex, col: colIndex });
    } else {
      e.dataTransfer.dropEffect = 'none';
    }
  };

  // 处理放置（优先使用组件状态draggedFrom/draggedStudent，与课程表一致）
  const handleDrop = (e, rowIndex, colIndex) => {
    e.preventDefault();
    e.stopPropagation();
    // 1) 与课程表一致：优先走“座位之间”的状态驱动交换
    if (draggedFromRef.current && draggedStudentRef.current) {
      const data = {
        fromRow: draggedFromRef.current.row,
        fromCol: draggedFromRef.current.col,
        student: draggedStudentRef.current,
      };
      handleSeatDrag(data, rowIndex, colIndex);
      setDragOverPosition(null);
      // 清理拖拽源状态
      draggedStudentRef.current = null;
      draggedFromRef.current = null;
      return;
    }

    // 2) 否则尝试“学生列表”拖入
    try {
      const textPlain = e.dataTransfer.getData('text/plain');
      if (textPlain) {
        const parsed = JSON.parse(textPlain);
        if (parsed && parsed.type === 'student') {
          handleStudentDrag(parsed, rowIndex, colIndex);
        }
      }
    } catch (err) {
      // ignore
    }
    setDragOverPosition(null);
  };

  // 处理座位间拖拽
  const handleSeatDrag = (data, targetRow, targetCol) => {
    const sourceRow = data.fromRow;
    const sourceCol = data.fromCol;
    
    // 不能拖到自己
    if (sourceRow === targetRow && sourceCol === targetCol) {
      return;
    }

    // 检查目标座位
    const targetSeat = seatingLayout[targetRow][targetCol];
    if (targetSeat.isEmpty) {
      notification.warning({
        message: '无法移动',
        description: '目标座位已被锁定',
      });
      return;
    }

    // 执行交换
    const newLayout = seatingLayout.map(row => row.map(seat => ({ ...seat })));
    const sourceStudent = newLayout[sourceRow][sourceCol].student;
    const targetStudent = newLayout[targetRow][targetCol].student;

    newLayout[targetRow][targetCol].student = sourceStudent;
    newLayout[sourceRow][sourceCol].student = targetStudent;

    onSeatingChange(newLayout);

    if (targetStudent) {
      notification.success({
        message: '交换成功',
        description: `${sourceStudent.name} 和 ${targetStudent.name} 已交换座位`,
      });
    } else {
      notification.success({
        message: '移动成功',
        description: `${sourceStudent.name} 已移动到新位置`,
      });
    }
    
  };

  // 处理从学生列表拖拽
  const handleStudentDrag = (student, targetRow, targetCol) => {
    const targetSeat = seatingLayout[targetRow][targetCol];
    if (targetSeat.isEmpty) {
      notification.warning({
        message: '无法移动',
        description: '该座位已被锁定',
      });
      return;
    }

    const newLayout = seatingLayout.map(row => row.map(seat => ({ ...seat })));
    
    // 清空学生在其他位置
    for (let r = 0; r < newLayout.length; r++) {
      for (let c = 0; c < newLayout[r].length; c++) {
        if (newLayout[r][c].student?.id === student.id) {
          newLayout[r][c].student = null;
        }
      }
    }
    
    // 安排到新位置
    newLayout[targetRow][targetCol].student = student;
    onSeatingChange(newLayout);
    
    notification.success({
      message: '安排成功',
      description: `${student.name} 已安排到座位 ${targetRow + 1}-${targetCol + 1}`,
    });
    
    console.log('✅ 学生安排完成');
  };

  // 处理右键菜单
  const handleContextMenu = (rowIndex, colIndex, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const seat = seatingLayout[rowIndex][colIndex];
    
    setContextMenu({
      show: true,
      x: e.pageX,
      y: e.pageY,
      rowIndex,
      colIndex,
      seat
    });
  };

  // 关闭右键菜单
  const closeContextMenu = () => {
    setContextMenu({ show: false, x: 0, y: 0, rowIndex: -1, colIndex: -1, seat: null });
  };

  // 锁定/解锁座位
  const toggleSeatLock = (rowIndex, colIndex) => {
    const newLayout = seatingLayout.map(row => row.map(seat => ({ ...seat })));
    const wasLocked = newLayout[rowIndex][colIndex].isEmpty;
    newLayout[rowIndex][colIndex].isEmpty = !newLayout[rowIndex][colIndex].isEmpty;
    
    // 如果锁定座位且有学生，清空学生
    if (!wasLocked && newLayout[rowIndex][colIndex].student) {
      newLayout[rowIndex][colIndex].student = null;
    }
    
    onSeatingChange(newLayout);
    closeContextMenu();
    
    notification.success({
      message: wasLocked ? '座位已解锁' : '座位已锁定',
      description: wasLocked ? '该座位现在可以安排学生' : '该座位已被锁定，无法安排学生',
    });
  };

  // 清空座位
  const clearSeat = (rowIndex, colIndex) => {
    const newLayout = seatingLayout.map(row => row.map(seat => ({ ...seat })));
    newLayout[rowIndex][colIndex].student = null;
    onSeatingChange(newLayout);
    closeContextMenu();
  };

  // 显示学生详情
  const showStudentDetail = (student) => {
    setCurrentStudent(student);
    setShowStudentModal(true);
    closeContextMenu();
  };

  // 点击外部关闭右键菜单
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.show) {
        closeContextMenu();
      }
    };

    if (contextMenu.show) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu.show]);

  // 获取座位样式
  const getSeatClassName = (rowIndex, colIndex, seat) => {
    let className = 'relative w-16 h-16 border-2 rounded-lg flex flex-col items-center justify-center text-xs transition-all duration-200';
    
    // 基础样式
    if (seat.isEmpty) {
      className += ' bg-gray-300 border-gray-400 cursor-not-allowed';
    } else if (seat.student) {
      className += ' bg-blue-50 border-blue-300 hover:bg-blue-100 cursor-move';
      
      // 检查是否是正在拖拽的座位（使用ref）
      if (draggedFromRef.current && draggedFromRef.current.row === rowIndex && draggedFromRef.current.col === colIndex) {
        className += ' opacity-50 transform scale-95';
      }
    } else {
      className += ' bg-white border-gray-300 hover:bg-gray-50 cursor-pointer';
    }
    
    // 拖拽悬停状态
    if (dragOverPosition && dragOverPosition.row === rowIndex && dragOverPosition.col === colIndex) {
      className += ' ring-2 ring-blue-400 bg-blue-100';
    }
    
    // 动画状态
    if (isArranging) {
      className += ' cursor-wait';
    }
    
    return className;
  };

  // 渲染座位内容
  const renderSeatContent = (seat, rowIndex, colIndex) => {
    if (seat.isEmpty) {
      return (
        <div className="flex flex-col items-center justify-center h-full w-full select-none pointer-events-none">
          <LockOutlined className="text-gray-500 text-lg" />
          <span className="text-xs text-gray-500 mt-1 select-none">锁定</span>
        </div>
      );
    }

    if (seat.student) {
      const { name, gender } = seat.student;
      return (
        <div className="flex flex-col items-center justify-center h-full w-full select-none pointer-events-none">
          <Avatar 
            size="small" 
            icon={<UserOutlined />}
            className={gender === 'female' ? 'bg-pink-500' : 'bg-blue-500'}
            style={{ pointerEvents: 'none' }}
          />
          <span className="text-xs mt-1 font-medium truncate w-full text-center select-none">
            {name}
          </span>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center h-full w-full text-gray-400 select-none pointer-events-none">
        <UserOutlined className="text-lg" />
        <span className="text-xs mt-1 select-none">空位</span>
      </div>
    );
  };

  return (
    <div className="p-6 overflow-visible">
      {/* 讲台 */}
      <div className="text-center mb-6">
        <div className="bg-gray-800 text-white py-3 px-8 rounded-lg inline-block text-lg font-medium">
          讲台
        </div>
      </div>

      {/* 操作提示 */}
      <div className="text-center mb-4">
        <div className="flex flex-wrap justify-center gap-2">
          <Tag color="blue" className="px-3 py-1">
            📖 从左侧拖拽学生到座位
          </Tag>
          <Tag color="green" className="px-3 py-1">
            🔄 拖拽座位中的学生互换位置
          </Tag>
          <Tag color="orange" className="px-3 py-1">
            🖱️ 右键管理座位
          </Tag>
          {draggedStudentRef.current && (
            <Tag color="purple" className="px-3 py-1 animate-pulse">
              🎯 拖拽中: {draggedStudentRef.current.name} - 释放到目标位置
            </Tag>
          )}
        </div>
      </div>

      {/* 座位布局 */}
      <div 
        className="overflow-x-auto overflow-y-visible"
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); }}
      >
        <div 
          className="grid gap-2 justify-center"
          style={{ 
            gridTemplateColumns: `repeat(${seatingLayout[0]?.length || 8}, 1fr)`,
            minWidth: 'fit-content'
          }}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); }}
        >
          {seatingLayout.map((row, rowIndex) =>
            row.map((seat, colIndex) => (
              <div
                key={`${rowIndex}-${colIndex}`}
                className={getSeatClassName(rowIndex, colIndex, seat)}
                draggable={!seat.isEmpty && !!seat.student}
                title={
                  seat.isEmpty 
                    ? `锁定座位 (${rowIndex + 1}-${colIndex + 1}) - 右键管理` 
                    : seat.student 
                      ? `${seat.student.name} (${rowIndex + 1}-${colIndex + 1}) - 右键查看详情`
                      : `空座位 (${rowIndex + 1}-${colIndex + 1}) - 右键锁定`
                }
                onDragStart={(e) => handleDragStart(e, rowIndex, colIndex)}
                onDragEnd={(e) => handleDragEnd(e)}
                onDragOver={(e) => handleDragOver(e, rowIndex, colIndex)}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDragLeave={(e) => { setDragOverPosition(null); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(e, rowIndex, colIndex); }}
                onContextMenu={(e) => handleContextMenu(rowIndex, colIndex, e)}
                style={{ userSelect: 'none' }}
              >
                <div style={{ pointerEvents: 'none' }}>
                  {renderSeatContent(seat, rowIndex, colIndex)}
                </div>
                
                {/* 座位号 */}
                {showSeatNumbers && (
                  <div 
                    className="absolute -bottom-1 -right-1 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
                    style={{ backgroundColor: seatNumberColor, pointerEvents: 'none' }}
                  >
                    {rowIndex * seatingLayout[0].length + colIndex + 1}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* 图例 */}
      <div className="mt-6 flex justify-center">
        <div className="flex gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-blue-50 border border-blue-300 rounded"></div>
            <span>已安排</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-white border border-gray-300 rounded"></div>
            <span>空位</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-gray-300 border border-gray-400 rounded"></div>
            <span>锁定</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 border-2 border-orange-500 rounded"></div>
            <span>已选择</span>
          </div>
        </div>
      </div>

      {/* 学生详情弹窗 */}
      <Modal
        title="学生信息"
        open={showStudentModal}
        onCancel={() => setShowStudentModal(false)}
        footer={null}
      >
        {currentStudent && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Avatar 
                size={64} 
                icon={<UserOutlined />}
                className={currentStudent.gender === 'female' ? 'bg-pink-500' : 'bg-blue-500'}
              />
              <div>
                <h3 className="text-xl font-bold">{currentStudent.name}</h3>
                <Tag color={currentStudent.gender === 'female' ? 'pink' : 'blue'}>
                  {currentStudent.gender === 'female' ? '女' : '男'}
                </Tag>
              </div>
            </div>
            
            {currentStudent.height && (
              <div>
                <strong>身高：</strong>{currentStudent.height}cm
              </div>
            )}
            
            {currentStudent.grade !== null && currentStudent.grade !== undefined && (
              <div>
                <strong>成绩：</strong>{currentStudent.grade}分
              </div>
            )}
            
            {currentStudent.notes && (
              <div>
                <strong>备注：</strong>{currentStudent.notes}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 右键菜单 */}
      {contextMenu.show && (
        <div
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 min-w-[120px]"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.seat?.student && (
            <>
              <button
                className="w-full px-4 py-2 text-left text-blue-600 hover:bg-blue-50 flex items-center gap-2 transition-colors duration-200"
                onClick={() => showStudentDetail(contextMenu.seat.student)}
              >
                <EyeOutlined />
                查看学生信息
              </button>
              <button
                className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors duration-200"
                onClick={() => clearSeat(contextMenu.rowIndex, contextMenu.colIndex)}
              >
                <DeleteOutlined />
                清空座位
              </button>
              <div className="border-t border-gray-200 my-1"></div>
            </>
          )}
          <button
            className="w-full px-4 py-2 text-left text-gray-600 hover:bg-gray-50 flex items-center gap-2 transition-colors duration-200"
            onClick={() => toggleSeatLock(contextMenu.rowIndex, contextMenu.colIndex)}
          >
            {contextMenu.seat?.isEmpty ? <UnlockOutlined /> : <LockOutlined />}
            {contextMenu.seat?.isEmpty ? '解锁座位' : '锁定座位'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SeatingLayout;
